import { put, takeLatest, all, call } from 'redux-saga/effects';

import { requestAndSet, sendToHelium } from 'utils/helpers';

import { emailTemplatesActions } from '../slices/emailTemplatesSlice';
import { EmailTemplate } from '../../types/emailTemplateTypes';

function* fetchEmailTemplatesSaga(
  action: ReturnType<typeof emailTemplatesActions.fetchEmailTemplates>,
): Generator {
  const { orgId } = action.payload;
  yield requestAndSet('listAllEmailTemplates', [orgId], {
    createSetterAction: (res: unknown) =>
      emailTemplatesActions.setTemplates(
        ((res as { data?: unknown[] }).data || []) as EmailTemplate[],
      ),
    loadingAction: (v: boolean) =>
      emailTemplatesActions.setLoading({
        key: 'fetchTemplatesLoading',
        value: v,
      }),
  });
}

function* createEmailTemplateSaga(
  action: ReturnType<typeof emailTemplatesActions.createEmailTemplate>,
): Generator {
  const { orgId, template } = action.payload;
  try {
    yield put(
      emailTemplatesActions.setLoading({
        key: 'createTemplateLoading',
        value: true,
      }),
    );
    const res = yield call(
      sendToHelium,
      'insertEmailTemplate',
      [orgId],
      template,
    );
    if (res) {
      yield put(emailTemplatesActions.fetchEmailTemplates({ orgId }));
    }
  } finally {
    yield put(
      emailTemplatesActions.setLoading({
        key: 'createTemplateLoading',
        value: false,
      }),
    );
  }
}

function* updateEmailTemplateSaga(
  action: ReturnType<typeof emailTemplatesActions.updateEmailTemplate>,
): Generator {
  const { orgId, templateId, template } = action.payload;
  try {
    yield put(
      emailTemplatesActions.setLoading({
        key: 'updateTemplateLoading',
        value: true,
      }),
    );
    yield call(
      sendToHelium,
      'updateEmailTemplate',
      [orgId, templateId],
      template,
    );
    yield put(emailTemplatesActions.fetchEmailTemplates({ orgId }));
  } finally {
    yield put(
      emailTemplatesActions.setLoading({
        key: 'updateTemplateLoading',
        value: false,
      }),
    );
  }
}

function* deleteEmailTemplateSaga(
  action: ReturnType<typeof emailTemplatesActions.deleteEmailTemplate>,
): Generator {
  const { orgId, templateId } = action.payload;
  try {
    yield put(
      emailTemplatesActions.setLoading({
        key: 'deleteTemplateLoading',
        value: true,
      }),
    );
    yield call(sendToHelium, 'deleteEmailTemplate', [orgId, templateId], {});
    yield put(emailTemplatesActions.fetchEmailTemplates({ orgId }));
  } finally {
    yield put(
      emailTemplatesActions.setLoading({
        key: 'deleteTemplateLoading',
        value: false,
      }),
    );
  }
}

export default function* emailTemplatesSaga(): Generator {
  yield all([
    takeLatest(
      emailTemplatesActions.fetchEmailTemplates.type,
      fetchEmailTemplatesSaga,
    ),
    takeLatest(
      emailTemplatesActions.createEmailTemplate.type,
      createEmailTemplateSaga,
    ),
    takeLatest(
      emailTemplatesActions.updateEmailTemplate.type,
      updateEmailTemplateSaga,
    ),
    takeLatest(
      emailTemplatesActions.deleteEmailTemplate.type,
      deleteEmailTemplateSaga,
    ),
  ]);
}
